import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Mic, MicOff, Loader2, Copy, Download, Stethoscope, FileText,
  ClipboardCheck, AlertTriangle, ShieldCheck, ArrowRight, ListChecks,
  ThumbsUp, ThumbsDown, Plus, Check,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Whisper (OpenAI whisper-1) for these; Sarvam for the Indic set — both offered "alongside".
const WHISPER_LANGS = { English: 'en', Spanish: 'es', French: 'fr', Portuguese: 'pt', Arabic: 'ar' };
const SARVAM_LANGS = { Hindi: 'hi-IN', Tamil: 'ta-IN', Telugu: 'te-IN', Kannada: 'kn-IN', Bengali: 'bn-IN', Marathi: 'mr-IN' };
const ALL_LANGS = [...Object.keys(WHISPER_LANGS), ...Object.keys(SARVAM_LANGS)];

function cleanVal(v) {
  return v && String(v).trim() && String(v).trim().toLowerCase() !== 'not discussed' ? String(v).trim() : '';
}

function buildSoapFromSections(sections) {
  const g = (k) => cleanVal(sections?.[k]);
  const S = [
    g('CHIEF_COMPLAINT') && `Chief complaint: ${g('CHIEF_COMPLAINT')}`,
    g('HISTORY_OF_PRESENT_ILLNESS'),
    g('REVIEW_OF_SYSTEMS') && `ROS: ${g('REVIEW_OF_SYSTEMS')}`,
    g('PAST_MEDICAL_HISTORY') && `PMH: ${g('PAST_MEDICAL_HISTORY')}`,
    g('MEDICATIONS') && `Medications: ${g('MEDICATIONS')}`,
    g('ALLERGIES') && `Allergies: ${g('ALLERGIES')}`,
    g('SOCIAL_HISTORY') && `Social history: ${g('SOCIAL_HISTORY')}`,
    g('FAMILY_HISTORY') && `Family history: ${g('FAMILY_HISTORY')}`,
  ].filter(Boolean).join('\n');
  const O = g('PHYSICAL_EXAM_FINDINGS');
  const A = g('PHYSICIAN_IMPRESSION');
  const P = [
    g('PLAN_DISCUSSED'),
    g('RED_FLAGS_MENTIONED') && `Red flags discussed: ${g('RED_FLAGS_MENTIONED')}`,
  ].filter(Boolean).join('\n');
  return { S, O, A, P };
}

// Derive ICD-10 from the physician's OWN dictated diagnosis (assessment), not
// from symptom flags — a physician note should carry the diagnosis the clinician
// actually made. Deterministic keyword map; unmatched dx degrade to "assign ICD".
function deriveIcds(impressionRaw, contextRaw = '') {
  const t = (impressionRaw || '').toLowerCase();
  if (!t.trim()) return [];
  const ctx = `${impressionRaw || ''} ${contextRaw || ''}`.toLowerCase();
  const out = [];
  const push = (code, description) => { if (!out.find((o) => o.code === code)) out.push({ code, description, source: 'assessment' }); };

  // Heart failure — resolve by ejection-fraction type + acuity for a precise code.
  if (/heart failure|cardiac failure|\bhf\b|hfref|hfpef|\bchf\b|decompensat/.test(t)) {
    const systolic = /systolic|reduced ejection|reduced ef|hfref|low ejection/.test(ctx);
    const diastolic = /diastolic|preserved ejection|preserved ef|hfpef/.test(ctx);
    const acute = /acute|decompensat|adhf|exacerbat/.test(ctx);
    const chronic = /chronic|known|history of|longstanding|established/.test(ctx);
    if (systolic) {
      if (acute && chronic) push('I50.23', 'Acute on chronic systolic (congestive) heart failure');
      else if (acute) push('I50.21', 'Acute systolic (congestive) heart failure');
      else if (chronic) push('I50.22', 'Chronic systolic (congestive) heart failure');
      else push('I50.20', 'Unspecified systolic (congestive) heart failure');
    } else if (diastolic) {
      if (acute && chronic) push('I50.33', 'Acute on chronic diastolic (congestive) heart failure');
      else if (acute) push('I50.31', 'Acute diastolic (congestive) heart failure');
      else if (chronic) push('I50.32', 'Chronic diastolic (congestive) heart failure');
      else push('I50.30', 'Unspecified diastolic (congestive) heart failure');
    } else {
      push('I50.9', 'Heart failure, unspecified');
    }
  }

  const simple = [
    [/\bnstemi\b/, 'I21.4', 'Non-ST elevation myocardial infarction (NSTEMI)'],
    [/\bstemi\b/, 'I21.3', 'ST elevation myocardial infarction (STEMI)'],
    [/myocardial infarction|heart attack|acute coronary/, 'I21.9', 'Acute myocardial infarction, unspecified'],
    [/unstable angina/, 'I20.0', 'Unstable angina'],
    [/\bangina\b/, 'I20.9', 'Angina pectoris, unspecified'],
    [/coronary artery disease|\bcad\b|ischemic heart/, 'I25.10', 'Atherosclerotic heart disease of native coronary artery'],
    [/atrial fibrillation|a-?fib/, 'I48.91', 'Atrial fibrillation, unspecified'],
    [/atrial flutter/, 'I48.92', 'Atrial flutter, unspecified'],
    [/cardiomyopathy/, 'I42.9', 'Cardiomyopathy, unspecified'],
    [/aortic stenosis/, 'I35.0', 'Nonrheumatic aortic (valve) stenosis'],
    [/hypertension|\bhtn\b|high blood pressure/, 'I10', 'Essential (primary) hypertension'],
    [/hyperlipidemia|dyslipidemia|high cholesterol/, 'E78.5', 'Hyperlipidemia, unspecified'],
    [/type 1 diabetes|\bt1dm\b/, 'E10.9', 'Type 1 diabetes mellitus without complications'],
    [/type 2 diabetes|\bt2dm\b|diabetes|diabetic/, 'E11.9', 'Type 2 diabetes mellitus without complications'],
    [/acute kidney injury|\baki\b|acute renal failure/, 'N17.9', 'Acute kidney failure, unspecified'],
    [/chronic kidney disease|\bckd\b/, 'N18.9', 'Chronic kidney disease, unspecified'],
    [/copd exacerbation|aecopd/, 'J44.1', 'COPD with (acute) exacerbation'],
    [/\bcopd\b|chronic obstructive/, 'J44.9', 'Chronic obstructive pulmonary disease, unspecified'],
    [/pneumonia/, 'J18.9', 'Pneumonia, unspecified organism'],
    [/asthma/, 'J45.909', 'Unspecified asthma, uncomplicated'],
    [/pulmonary embolism|\bpe\b/, 'I26.99', 'Pulmonary embolism without acute cor pulmonale'],
    [/deep vein thrombosis|\bdvt\b/, 'I82.409', 'Acute DVT of unspecified deep veins of lower extremity'],
    [/ischemic stroke|cerebral infarction|\bcva\b/, 'I63.9', 'Cerebral infarction, unspecified'],
    [/\btia\b|transient ischemic/, 'G45.9', 'Transient cerebral ischemic attack, unspecified'],
    [/urinary tract infection|\buti\b/, 'N39.0', 'Urinary tract infection, site not specified'],
    [/sepsis/, 'A41.9', 'Sepsis, unspecified organism'],
    [/anemia/, 'D64.9', 'Anemia, unspecified'],
    [/hypothyroid/, 'E03.9', 'Hypothyroidism, unspecified'],
    [/\bgerd\b|gastroesophageal reflux/, 'K21.9', 'GERD without esophagitis'],
    [/chest pain/, 'R07.9', 'Chest pain, unspecified'],
  ];
  simple.forEach(([re, code, desc]) => { if (re.test(t)) push(code, desc); });
  return out;
}

const sevColor = (sev) => {
  const s = (sev || '').toLowerCase();
  if (s.includes('high') || s.includes('critical') || s.includes('red')) return 'bg-red-100 text-red-700 border-red-200';
  if (s.includes('mod') || s.includes('medium') || s.includes('amber') || s.includes('yellow')) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

// Flag Relevance filter — a safety/guideline flag is shown only when at least one
// of its engine trigger tokens appears as a WHOLE WORD/phrase in what was actually
// dictated. Whole-word matching (not substring) is what removes stray hits like a
// "DIC" flag firing on the "dic" inside "medical" for a clean heart-failure case.
function tokenInText(tok, hay) {
  const esc = String(tok).toLowerCase().trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (!esc) return false;
  return new RegExp(`(?:^|[^a-z0-9])${esc}(?:[^a-z0-9]|$)`, 'i').test(hay);
}
function flagIsRelevant(flag, transcript) {
  const triggers = (flag.triggers || []).map((t) => String(t || '').trim()).filter(Boolean);
  if (!triggers.length) return true; // no trigger to disprove relevance — keep the safety flag
  const hay = ` ${(transcript || '').toLowerCase()} `;
  return triggers.some((tok) => tokenInText(tok, hay));
}

export default function PhysicianScribePage() {
  // Identity gate (same idea as validators — name/email first, open to anyone)
  const [started, setStarted] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [specialty, setSpecialty] = useState('');

  const [language, setLanguage] = useState('English');
  const [age, setAge] = useState('');
  const [sex, setSex] = useState('');

  const [transcript, setTranscript] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const [soap, setSoap] = useState(null); // { S,O,A,P } editable
  const [engine, setEngine] = useState(null); // { icds, flags, differentials }
  const [encounterId, setEncounterId] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const startTool = () => {
    if (!name.trim() || !email.trim()) {
      toast.error('Please enter your name and email to begin.');
      return;
    }
    setStarted(true);
  };

  const toggleRecording = () => {
    if (isRecording) {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      setIsRecording(false);
      return;
    }
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then((stream) => {
        const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        mediaRecorderRef.current = mr;
        audioChunksRef.current = [];
        setIsRecording(true);
        mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
        mr.onstop = async () => {
          stream.getTracks().forEach((t) => t.stop());
          setIsRecording(false);
          const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          if (blob.size < 1500) {
            toast.error('Recording too short — please speak for a few seconds.');
            return;
          }
          await sendAudio(blob);
        };
        mr.start();
      })
      .catch(() => toast.error('Microphone access denied. You can type or paste your dictation instead.'));
  };

  const sendAudio = async (blob) => {
    setIsTranscribing(true);
    try {
      const useWhisper = Object.keys(WHISPER_LANGS).includes(language);
      const url = useWhisper ? `${API}/scribe/whisper-transcribe` : `${API}/ambient/transcribe`;
      const fd = new FormData();
      fd.append('file', blob, 'dictation.webm');
      if (useWhisper) fd.append('language', WHISPER_LANGS[language]);
      else fd.append('language_code', SARVAM_LANGS[language]);
      const res = await fetch(url, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok || data.error) {
        toast.error(data.error || 'Transcription failed. Please type/paste instead.');
      } else {
        const text = (data.transcript || '').trim();
        setTranscript((prev) => (prev ? prev + ' ' : '') + text);
        toast.success(`Transcribed via ${useWhisper ? 'Whisper' : 'Sarvam'}.`);
      }
    } catch (e) {
      toast.error('Transcription failed. Please type/paste your dictation.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const generateNote = async () => {
    if (transcript.trim().length < 20) {
      toast.error('Please dictate or type at least a few sentences first.');
      return;
    }
    setIsGenerating(true);
    setSoap(null);
    setEngine(null);
    try {
      const res = await fetch(`${API}/ambient/generate-soap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcript: transcript.trim(), age, sex, language }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        toast.error(data.error || 'Could not generate the note. Please try again.');
        return;
      }
      const sections = data?.ambientScribe?.structuredSections || {};
      setSoap(buildSoapFromSections(sections));
      setEncounterId(data?.encounterId || '');
      setFeedbackSent(false);
      const impression = cleanVal(sections?.PHYSICIAN_IMPRESSION);
      let icds = deriveIcds(impression, transcript);
      if (icds.length === 0 && impression) {
        icds = [{ code: '—', description: `${impression} (assign ICD-10)`, source: 'assessment' }];
      }
      const rawFlags = (data?.riskFlags || []).map((f) => ({
        category: f.category || f.title || 'Flag',
        severity: f.severity || '',
        message: f.message || f.detail || '',
        triggers: f.triggers || [],
      }));
      // Relevance filter — keep only flags tied to what was actually dictated.
      const flags = rawFlags.filter((f) => flagIsRelevant(f, transcript));
      const hiddenFlagCount = rawFlags.length - flags.length;
      const differentials = (data?.differentials || []).map((d) =>
        d.condition || d.name || d.diagnosis || d.label || ''
      ).filter(Boolean);
      setEngine({ icds, flags, differentials, hiddenFlagCount });
      toast.success('Draft note generated — please review.');
    } catch (e) {
      toast.error('Could not generate the note. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const noteText = () => {
    if (!soap) return '';
    return (
      `SOAP NOTE — DRAFT · CLINICIAN REVIEW\n` +
      `Clinician: ${name}${specialty ? ` (${specialty})` : ''}\n` +
      `${age || sex ? `Patient: ${age || '?'} / ${sex || '?'}\n` : ''}` +
      `\nS (Subjective):\n${soap.S || '—'}\n` +
      `\nO (Objective):\n${soap.O || '—'}\n` +
      `\nA (Assessment):\n${soap.A || '—'}\n` +
      `\nP (Plan):\n${soap.P || '—'}`
    );
  };

  const copyNote = async () => {
    const text = noteText();
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Note copied — paste it into your EMR.');
    } catch (e) {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        toast.success('Note copied — paste it into your EMR.');
      } catch (e2) {
        toast.error('Could not copy automatically — please select the note text and copy manually.');
      }
    }
  };

  const downloadNote = () => {
    const blob = new Blob([noteText()], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `soap-draft-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded .txt for your EMR.');
  };

  const sendUsefulness = async (rating) => {
    try {
      await fetch(`${API}/scribe/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ encounter_id: encounterId, rating, name, email, specialty }),
      });
      setFeedbackSent(true);
      toast.success('Thank you for the feedback!');
    } catch (e) {
      toast.error('Could not send feedback — please try again.');
    }
  };

  const setSoapField = (k, v) => setSoap((prev) => ({ ...prev, [k]: v }));

  // One-Tap Accept — drop a suggested ICD into the Assessment, or a safety flag into the Plan.
  const addIcdToNote = (icd) => {
    if (!icd || !icd.code || icd.code === '—') return;
    const line = `${icd.code}${icd.description || icd.label ? ` — ${icd.description || icd.label}` : ''}`;
    const already = (soap?.A || '').includes(icd.code);
    setSoap((prev) => {
      const cur = prev.A || '';
      if (cur.includes(icd.code)) return prev;
      return { ...prev, A: cur ? `${cur}\n${line}` : line };
    });
    if (already) toast(`${icd.code} is already in your Assessment.`);
    else toast.success(`${icd.code} added to Assessment.`);
  };
  const addFlagToNote = (flag) => {
    if (!flag) return;
    const line = `${flag.category}: ${flag.message}`;
    const key = (flag.message || flag.category || '').slice(0, 40);
    const already = key && (soap?.P || '').includes(key);
    setSoap((prev) => {
      const cur = prev.P || '';
      if (key && cur.includes(key)) return prev;
      return { ...prev, P: cur ? `${cur}\n${line}` : line };
    });
    if (already) toast('This flag is already in your Plan.');
    else toast.success('Flag added to Plan.');
  };

  // ---------- Identity gate ----------
  if (!started) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="physician-scribe-page">
        <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <div className="flex items-center gap-2 text-[#2563EB] mb-1">
            <Stethoscope className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-wider">Physician Dictation · Demo</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Dictate your note</h1>
          <p className="text-sm text-slate-500 mb-6">
            Speak the encounter in your own words. We structure it into a SOAP note with draft ICD codes for you to
            review, edit, and paste into your EMR. Optional — use it or keep your own workflow.
          </p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Name</label>
              <Input data-testid="scribe-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Dr. Full Name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Email</label>
              <Input data-testid="scribe-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.org" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Specialty <span className="text-slate-400">(optional)</span></label>
              <Input data-testid="scribe-specialty-input" value={specialty} onChange={(e) => setSpecialty(e.target.value)} placeholder="e.g., Cardiology / Hospital Medicine" />
            </div>
          </div>
          <Button data-testid="scribe-start-btn" onClick={startTool} className="w-full mt-6 bg-[#2563EB] hover:bg-blue-700 rounded-lg h-11">
            Start <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          <p className="text-[11px] text-slate-400 mt-4 leading-relaxed">
            Synthetic demo · no PHI. Suggestions are drafts for clinician review — you remain the author of your note.
          </p>
        </div>
      </div>
    );
  }

  // ---------- Tool ----------
  return (
    <div className="min-h-screen bg-slate-50" data-testid="physician-scribe-page">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Stethoscope className="w-6 h-6 text-[#2563EB]" />
            <div>
              <h1 className="text-xl font-bold text-slate-900 leading-tight">Physician Dictation — Demo</h1>
              <p className="text-xs text-slate-500">{name}{specialty ? ` · ${specialty}` : ''}</p>
            </div>
          </div>
          <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 font-semibold">Draft · Clinician Review</Badge>
        </div>

        {/* Step 1 — capture */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-5">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-2 text-slate-800 font-semibold text-sm">
              <Mic className="w-4 h-4 text-[#2563EB]" /> 1 · Dictate the encounter
            </div>
            <div className="flex items-center gap-2">
              <select
                data-testid="scribe-language-select"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white"
              >
                {ALL_LANGS.map((l) => (
                  <option key={l} value={l}>{l}{Object.keys(WHISPER_LANGS).includes(l) ? ' (Whisper)' : ' (Sarvam)'}</option>
                ))}
              </select>
              <Input className="w-16" data-testid="scribe-age-input" value={age} onChange={(e) => setAge(e.target.value)} placeholder="Age" />
              <Input className="w-16" data-testid="scribe-sex-input" value={sex} onChange={(e) => setSex(e.target.value)} placeholder="Sex" />
            </div>
          </div>

          <div className="flex items-center gap-3 mb-3">
            <Button
              data-testid="scribe-record-btn"
              onClick={toggleRecording}
              disabled={isTranscribing}
              className={`rounded-lg h-10 ${isRecording ? 'bg-red-600 hover:bg-red-700' : 'bg-[#2563EB] hover:bg-blue-700'}`}
            >
              {isRecording ? <><MicOff className="w-4 h-4 mr-1" /> Stop</> : <><Mic className="w-4 h-4 mr-1" /> Record</>}
            </Button>
            {isTranscribing && <span className="text-sm text-slate-500 flex items-center gap-1"><Loader2 className="w-4 h-4 animate-spin" /> Transcribing…</span>}
            {isRecording && <span className="text-sm text-red-600 flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-600 animate-pulse" /> Listening…</span>}
            <span className="text-xs text-slate-400">or type / paste below</span>
          </div>

          <Textarea
            data-testid="scribe-transcript-input"
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            placeholder="e.g., 'This is a 68-year-old man with known HFrEF here for worsening dyspnea over 4 days, orthopnea, 3 kg weight gain. Exam: JVP elevated, bibasilar crackles, 2+ edema. Assessment: acute decompensated heart failure. Plan: IV furosemide, daily weights, up-titrate GDMT, echo…'"
            className="min-h-[140px] text-sm"
          />

          <div className="flex justify-end mt-3">
            <Button
              data-testid="scribe-generate-btn"
              onClick={generateNote}
              disabled={isGenerating}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-lg h-10"
            >
              {isGenerating ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generating…</> : <><FileText className="w-4 h-4 mr-1" /> Generate note</>}
            </Button>
          </div>
        </div>

        {/* Step 2 — output */}
        {soap && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Physician note — authoritative */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-5" data-testid="scribe-soap-note">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2 text-slate-800 font-semibold text-sm">
                  <ClipboardCheck className="w-4 h-4 text-[#2563EB]" /> 2 · Your note — draft, edit freely
                </div>
                <div className="flex gap-2">
                  <Button data-testid="scribe-copy-btn" onClick={copyNote} variant="outline" className="h-9 rounded-lg text-sm">
                    <Copy className="w-4 h-4 mr-1" /> Copy
                  </Button>
                  <Button data-testid="scribe-download-btn" onClick={downloadNote} variant="outline" className="h-9 rounded-lg text-sm">
                    <Download className="w-4 h-4 mr-1" /> .txt
                  </Button>
                </div>
              </div>
              {[
                { k: 'S', label: 'S · Subjective', testid: 'scribe-soap-subjective' },
                { k: 'O', label: 'O · Objective', testid: 'scribe-soap-objective' },
                { k: 'A', label: 'A · Assessment (your judgment)', testid: 'scribe-soap-assessment' },
                { k: 'P', label: 'P · Plan (your orders)', testid: 'scribe-soap-plan' },
              ].map(({ k, label, testid }) => (
                <div key={k} className="mb-3">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{label}</label>
                  <Textarea
                    data-testid={testid}
                    value={soap[k] || ''}
                    onChange={(e) => setSoapField(k, e.target.value)}
                    className="mt-1 text-sm min-h-[70px]"
                  />
                </div>
              ))}
              <p className="text-[11px] text-slate-400 mt-1">Your dictated Assessment &amp; Plan lead the note. The engine only suggests coding &amp; safety checks (right) — it does not overrule you.</p>
            </div>

            {/* Right column: transcript + engine review */}
            <div className="space-y-5">
              <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="scribe-transcript-panel">
                <div className="flex items-center gap-2 text-slate-800 font-semibold text-sm mb-2">
                  <FileText className="w-4 h-4 text-slate-500" /> Verbatim transcript
                </div>
                <p className="text-xs text-slate-600 whitespace-pre-wrap max-h-40 overflow-auto">{transcript}</p>
                <p className="text-[11px] text-slate-400 mt-2">Compare against the note to confirm nothing was mis-captured.</p>
              </div>

              <div className="bg-white rounded-2xl border border-emerald-200 p-5" data-testid="scribe-engine-review">
                <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm mb-1">
                  <ShieldCheck className="w-4 h-4" /> Engine review — suggestions only
                </div>
                <p className="text-[11px] text-slate-500 mb-3">Deterministic coding &amp; safety checks. Drafts for your consideration — they support, not replace, your assessment.</p>

                <div className="mb-3" data-testid="scribe-icd-list">
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1 flex items-center justify-between">
                    <span>ICD-10 (draft) — from your assessment</span>
                    {engine?.icds?.some((i) => i.code && i.code !== '—') && (
                      <span className="text-[10px] font-normal normal-case text-emerald-600">tap to add →</span>
                    )}
                  </div>
                  {engine?.icds?.length ? engine.icds.map((i, idx) => {
                    const addable = !!(i.code && i.code !== '—');
                    const added = addable && (soap?.A || '').includes(i.code);
                    return (
                      <button
                        key={idx}
                        type="button"
                        data-testid={`scribe-icd-accept-${i.code}`}
                        onClick={() => addIcdToNote(i)}
                        disabled={!addable}
                        className={`w-full text-left text-sm flex items-center gap-2 rounded-md px-1.5 py-1 transition-colors ${addable ? 'hover:bg-emerald-50 cursor-pointer' : 'cursor-default'}`}
                      >
                        <span className="font-mono font-semibold text-emerald-700">{i.code}</span>
                        <span className="text-slate-500 flex-1">{i.description || i.label || ''}</span>
                        {addable && (added
                          ? <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                          : <Plus className="w-3.5 h-3.5 text-emerald-500 shrink-0" />)}
                      </button>
                    );
                  }) : <div className="text-sm text-slate-400">No code suggestions.</div>}
                </div>

                <div className="mb-2" data-testid="scribe-flags-list">
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Safety / guideline flags</div>
                  {engine?.flags?.length ? engine.flags.map((f, idx) => {
                    const key = (f.message || f.category || '').slice(0, 40);
                    const added = key && (soap?.P || '').includes(key);
                    return (
                      <div key={idx} className="mb-1.5 flex items-start gap-1.5">
                        <div className="flex-1">
                          <Badge variant="outline" className={`text-[11px] mr-1 ${sevColor(f.severity)}`}>{f.category}</Badge>
                          <span className="text-sm text-slate-600">{f.message}</span>
                        </div>
                        <button
                          type="button"
                          data-testid={`scribe-flag-accept-${idx}`}
                          onClick={() => addFlagToNote(f)}
                          title="Add to Plan"
                          className="shrink-0 mt-0.5 text-emerald-500 hover:text-emerald-700"
                        >
                          {added ? <Check className="w-4 h-4 text-emerald-600" /> : <Plus className="w-4 h-4" />}
                        </button>
                      </div>
                    );
                  }) : <div className="text-sm text-slate-400 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> No flags matched your dictation.</div>}
                  {engine?.hiddenFlagCount > 0 && (
                    <div className="text-[11px] text-slate-400 mt-1" data-testid="scribe-flags-hidden">
                      {engine.hiddenFlagCount} unrelated flag{engine.hiddenFlagCount > 1 ? 's' : ''} hidden (not tied to your dictation)
                    </div>
                  )}
                </div>

                {engine?.differentials?.length > 0 && (
                  <div data-testid="scribe-ddx-list">
                    <div className="text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-1"><ListChecks className="w-3.5 h-3.5" /> Differentials (consider)</div>
                    <div className="text-sm text-slate-600">{engine.differentials.slice(0, 6).join(', ')}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {soap && (
          <div className="mt-5 bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3 flex-wrap" data-testid="scribe-feedback-bar">
            {feedbackSent ? (
              <span className="text-sm text-emerald-700 font-medium flex items-center gap-1"><ThumbsUp className="w-4 h-4" /> Thanks — your feedback was recorded.</span>
            ) : (
              <>
                <span className="text-sm font-semibold text-slate-700">Was this note useful?</span>
                <Button data-testid="scribe-feedback-up" onClick={() => sendUsefulness('up')} variant="outline" className="h-9 rounded-lg text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                  <ThumbsUp className="w-4 h-4 mr-1" /> Useful
                </Button>
                <Button data-testid="scribe-feedback-down" onClick={() => sendUsefulness('down')} variant="outline" className="h-9 rounded-lg text-slate-600">
                  <ThumbsDown className="w-4 h-4 mr-1" /> Not useful
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
